import { ArrowLeft, Mail, Phone, MapPin, Edit, Microscope, BookOpen, Plus, Tag } from 'lucide-react';
import { useContact } from '../hooks/useContacts';
import { Badge } from '../components/shared/Badge';
import { StatusDot } from '../components/shared/StatusDot';
import { EmptyState } from '../components/shared/EmptyState';

interface ContactDetailViewProps {
  contactId: string;
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

export function ContactDetailView({ contactId, onNavigate }: ContactDetailViewProps) {
  const { data: contact, isLoading, error } = useContact(contactId === 'new' ? null : contactId);

  if (contactId === 'new') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('contacts')} className="btn-ghost rounded-full p-1.5">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-xl font-bold text-slate-900">New Contact</h1>
          </div>
        </div>
        <div className="p-6">
          <div className="card max-w-2xl p-6">
            <p className="text-sm text-slate-500">
              Contact creation form will be implemented here. Use the API to create contacts via POST /api/contacts.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-surface-200" />
          <div className="h-32 rounded-lg bg-surface-200" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-64 rounded-lg bg-surface-200" />
            <div className="h-64 rounded-lg bg-surface-200" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex-1 p-6">
        <EmptyState
          icon={Mail}
          title="Contact not found"
          description="This contact may have been deleted or you may not have access."
          actionLabel="Back to Contacts"
          onAction={() => onNavigate('contacts')}
        />
      </div>
    );
  }

  const initials = `${contact.firstName?.[0] ?? ''}${contact.lastName?.[0] ?? ''}`;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('contacts')} className="btn-ghost rounded-full p-1.5">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-lg font-bold text-brand-700">
              {initials}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {contact.firstName} {contact.lastName}
              </h1>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                {contact.title && <span>{contact.title}</span>}
                {contact.title && contact.organizationName && <span>at</span>}
                {contact.organizationName && (
                  <button
                    onClick={() => onNavigate('org-detail', { orgId: contact.organizationId! })}
                    className="text-brand-600 hover:underline"
                  >
                    {contact.organizationName}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn-secondary btn-sm">
            <Edit className="h-3.5 w-3.5" />
            Edit
          </button>
          <button className="btn-secondary btn-sm">
            <Microscope className="h-3.5 w-3.5" />
            Research
          </button>
          <button className="btn-secondary btn-sm">
            <BookOpen className="h-3.5 w-3.5" />
            Generate Dossier
          </button>
          <button className="btn-primary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add Note
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-3">
        {/* Left Column — Contact Info */}
        <div className="space-y-4 lg:col-span-1">
          {/* Status & Role */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Details</h3>
            <div className="space-y-3">
              {contact.status && (
                <div className="flex items-center justify-between">
                  <span className="label">Status</span>
                  <StatusDot status={contact.status} label={contact.status} />
                </div>
              )}
              {contact.insuranceRole && (
                <div className="flex items-center justify-between">
                  <span className="label">Role</span>
                  <Badge color="blue">{contact.insuranceRole}</Badge>
                </div>
              )}
              {contact.source && (
                <div className="flex items-center justify-between">
                  <span className="label">Source</span>
                  <span className="text-sm text-slate-600">{contact.source}</span>
                </div>
              )}
            </div>
          </div>

          {/* Contact Methods */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Contact Info</h3>
            <div className="space-y-3">
              {contact.emails?.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-slate-700">{e.value}</span>
                  <Badge color="gray" size="sm">{e.type}</Badge>
                </div>
              ))}
              {contact.phones?.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-slate-700">{p.value}</span>
                  <Badge color="gray" size="sm">{p.type}</Badge>
                </div>
              ))}
              {contact.addresses?.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
                  <div>
                    <div className="text-slate-700">{a.street}</div>
                    <div className="text-slate-500">{a.city}, {a.state} {a.zip}</div>
                    <Badge color="gray" size="sm">{a.type}</Badge>
                  </div>
                </div>
              ))}
              {!contact.emails?.length && !contact.phones?.length && !contact.addresses?.length && (
                <p className="text-sm text-slate-400">No contact information available.</p>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Tags</h3>
              <button className="btn-ghost btn-sm p-1">
                <Tag className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {contact.tags?.map((tag) => (
                <Badge key={tag} color="blue" size="md">{tag}</Badge>
              ))}
              {(!contact.tags || contact.tags.length === 0) && (
                <p className="text-sm text-slate-400">No tags</p>
              )}
            </div>
          </div>

          {/* Custom Fields */}
          {contact.customFields && Object.keys(contact.customFields).length > 0 && (
            <div className="card p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Custom Fields</h3>
              <div className="space-y-2">
                {Object.entries(contact.customFields).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="label">{key}</span>
                    <span className="text-sm text-slate-600">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column — Activity & Notes */}
        <div className="space-y-4 lg:col-span-2">
          {/* Notes */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Notes</h3>
            {contact.notes ? (
              <p className="whitespace-pre-wrap text-sm text-slate-600">{contact.notes}</p>
            ) : (
              <p className="text-sm text-slate-400">No notes yet. Click "Add Note" to get started.</p>
            )}
          </div>

          {/* Activity Timeline placeholder */}
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Activity Timeline</h3>
            <div className="py-8 text-center text-sm text-slate-400">
              Activity timeline will appear here once interactions are logged.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
